import { ROUTES } from "@/constants/Routes";
import { ATTRIBUTION_NAMES } from "@/constants/User";
import { hasAttribution } from "@/utils/permissions";
import { useUserStore } from "@/store/useUserStore";
import theme from "@/styles/theme";
import styled from "@emotion/styled";
import { InfoOutlined, KeyboardArrowDownSharp } from "@mui/icons-material";
import {
	Alert,
	Box,
	Collapse,
	Divider,
	Link,
	Step,
	StepIcon,
	StepLabel,
	Stepper,
	Typography,
} from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import Image from "next/image";
import { useState } from "react";
import questionnaire_register_step_two from "@/assets/images/questionnaire_register_step_two.png";
import { CustomTooltip } from "@/components/CustomTooltip";

const CustomStepIcon = styled(StepIcon)(() => ({
	color: theme.palette.raip.main,
	fontWeight: 600,
	"&.Mui-active": {
		color: theme.palette.raip.main,
		fontWeight: 600,
	},
}));

const STEPS = ["Configurações", "Questionário", "Revisão"];

export function AIAQuestionnaireRegister() {
	const [expanded, setExpanded] = useState({
		configurations: false,
		questionnaire: false,
		review: false,
	});

	const canManage = useUserStore(
		({ attributions, isAdmin }) =>
			hasAttribution(attributions ?? [], ATTRIBUTION_NAMES.SOCIO_JURIDICO) || isAdmin
	);

	return (
		<>
			<Alert severity='warning'>
				Somente administradores e especialistas sócio-jurídicos podem cadastrar
				novos questionários AIA
			</Alert>

			<Typography variant='body2'>
				O questionário AIA é composto por{" "}
				{canManage ? (
					<Link href={ROUTES.SECTION.REGISTER}>seções</Link>
				) : (
					<span style={{ fontWeight: 600 }}>seções</span>
				)}
				,{" "}
				{canManage ? (
					<Link href={ROUTES.QUESTION.REGISTER}>questões</Link>
				) : (
					<span style={{ fontWeight: 600 }}>questões</span>
				)}{" "}
				(escolha única e múltipla escolha) e{" "}
				{canManage ? (
					<Link href={ROUTES.PRINCIPLE.REGISTER}>princípios éticos</Link>
				) : (
					<span style={{ fontWeight: 600 }}>princípios éticos</span>
				)}
				. Além da{" "}
				{canManage ? (
					<Link href={ROUTES.AIA.REGISTER}>AIA</Link>
				) : (
					<span style={{ fontWeight: 600 }}>AIA</span>
				)}{" "}
				para a qual se deseja vincular,{" "}
				<span style={{ fontWeight: 600 }}>todas</span> as entidades devem estar
				previamente cadastradas para que o questionário possa ser devidamente
				cadastrado.
			</Typography>

			<Typography variant='body2'>
				O fluxo de{" "}
				{canManage ? (
					<Link href={ROUTES.QUESTIONNAIRE.REGISTER.AIA}>
						cadastro de questionário AIA
					</Link>
				) : (
					"cadastro de questionário AIA"
				)}{" "}
				cumpre algumas etapas:
			</Typography>

			<Stepper alternativeLabel>
				{STEPS.map((label) => (
					<Step key={label}>
						<StepLabel
							slotProps={{
								label: {
									sx: {
										marginTop: 0.8,
										fontSize: "13px",
										color: blueGrey[700],
										userSelect: "none",
									},
								},
							}}
							slots={{ stepIcon: CustomStepIcon, label: Typography }}
						>
							{label}
						</StepLabel>
					</Step>
				))}
			</Stepper>

			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 2,
				}}
			>
				<Box>
					<Box
						sx={{
							display: "flex",
							width: "100%",
							alignItems: "center",
							maxWidth: "100%",
							overflow: "hidden",
							gap: 1,
							cursor: "pointer",
						}}
						onClick={() =>
							setExpanded({
								...expanded,
								configurations: !expanded.configurations,
							})
						}
					>
						<Typography variant='body2' fontWeight={600}>
							1. Configurações
						</Typography>

						<Divider sx={{ flex: 1, borderColor: blueGrey[100] }} />

						<KeyboardArrowDownSharp
							fontSize='small'
							sx={{
								color: blueGrey[500],
								width: "1.25rem",
								height: "1.25rem",
								transition: "transform 0.3s",
								transform: expanded.configurations
									? "rotate(180deg)"
									: "rotate(0deg)",
							}}
						/>
					</Box>

					<Collapse in={expanded.configurations} timeout={"auto"} unmountOnExit>
						<Box sx={{ paddingTop: 0.5, paddingInline: 2, paddingBottom: 1 }}>
							<Typography variant='body2'>
								Definição da AIA a ser vinculada ao novo questionário, assim
								como as informações base para o cadastro:
							</Typography>

							<ul
								style={{
									margin: 0,
									paddingLeft: "1.25rem",
									paddingTop: "0.2rem",
									display: "flex",
									flexDirection: "column",
									gap: 3,
								}}
							>
								<li>
									<Typography variant='body2'>Princípios Éticos</Typography>
									<ul
										style={{
											paddingLeft: "1.25rem",
											display: "flex",
											flexDirection: "column",
											gap: 3,
										}}
									>
										<li>
											<Typography variant='body2' fontSize={"0.85rem"}>
												Pelo menos três princípios devem ser selecionados.
											</Typography>
										</li>
									</ul>
								</li>
								<li>
									<Typography variant='body2'>Descrição</Typography>
								</li>
								<li>
									<Typography variant='body2'>Justificativa</Typography>
								</li>
								<ul
									style={{
										paddingLeft: "1.25rem",
										display: "flex",
										flexDirection: "column",
										gap: 3,
									}}
								>
									<li>
										<Typography variant='body2' fontSize={"0.85rem"}>
											Motivo pelo qual o questionário está sendo cadastrado. O
											Comitê de Avaliação irá utilizar essa informação para
											entender o contexto do questionário.
										</Typography>
									</li>
								</ul>
							</ul>
						</Box>
					</Collapse>
				</Box>

				<Box>
					<Box
						sx={{
							display: "flex",
							width: "100%",
							alignItems: "center",
							maxWidth: "100%",
							overflow: "hidden",
							gap: 1,
							cursor: "pointer",
						}}
						onClick={() =>
							setExpanded({
								...expanded,
								questionnaire: !expanded.questionnaire,
							})
						}
					>
						<Typography variant='body2' fontWeight={600}>
							2. Questionário
						</Typography>

						<Divider sx={{ flex: 1, borderColor: blueGrey[100] }} />

						<KeyboardArrowDownSharp
							fontSize='small'
							sx={{
								color: blueGrey[500],
								width: "1.25rem",
								height: "1.25rem",
								transition: "transform 0.3s",
								transform: expanded.questionnaire
									? "rotate(180deg)"
									: "rotate(0deg)",
							}}
						/>
					</Box>

					<Collapse in={expanded.questionnaire} timeout={"auto"} unmountOnExit>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 1,
								paddingTop: 0.5,
								paddingInline: 2,
								paddingBottom: 1,
							}}
						>
							<Typography variant='body2'>
								Construção do corpo do questionário. Para isso são adicionadas
								seções, questões e princípio éticos ao formulário.
							</Typography>

							<Box>
								<Image
									src={questionnaire_register_step_two}
									alt='Exemplo de registro de questionário AIA - etapa 2'
									loading='lazy'
									style={{
										borderRadius: "0.25rem",
										maxWidth: "100%",
									}}
									width={480}
								/>
								<Typography
									component={"p"}
									variant='caption'
									color={blueGrey[500]}
									lineHeight={"0.9rem"}
									marginTop={1}
								>
									Registro de questionário AIA (2ª etapa) - Questão sem
									princípios éticos associados
								</Typography>
							</Box>

							<Box>
								<Typography variant='body2'>
									Algumas condições e funcionalidades devem ser observadas:
								</Typography>

								<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
									<ul
										style={{
											margin: 0,
											paddingLeft: "1.5rem",
											paddingTop: "0.2rem",
											display: "flex",
											flexDirection: "column",
											gap: 3,
										}}
									>
										<li>
											<Typography variant='body2'>
												<span style={{ fontWeight: 600 }}>Seções</span>
											</Typography>
											<ul
												style={{
													margin: 0,
													paddingLeft: "1.5rem",
													paddingTop: "0.2rem",
													display: "flex",
													flexDirection: "column",
													gap: 3,
												}}
											>
												<li>
													<Typography variant='body2'>
														Devem ser adicionadas ao questionário
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														Deve haver pelo menos uma seção
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														Não podem haver seções repetidas
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														Deve conter pelo menos uma questão
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														A remoção de uma seção remove todas as questões
														vinculadas a ela no questionário
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														É possível pesquisar e cadastrar novas seções
													</Typography>
												</li>
											</ul>
										</li>
									</ul>
									<ul
										style={{
											margin: 0,
											paddingLeft: "1.5rem",
											paddingTop: "0.2rem",
											display: "flex",
											flexDirection: "column",
											gap: 3,
										}}
									>
										<li>
											<Typography variant='body2'>
												<span style={{ fontWeight: 600 }}>Questões</span>
											</Typography>
											<ul
												style={{
													margin: 0,
													paddingLeft: "1.5rem",
													paddingTop: "0.2rem",
													display: "flex",
													flexDirection: "column",
													gap: 3,
												}}
											>
												<li>
													<Typography variant='body2'>
														Devem ser adicionadas ao questionário
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														Questões dos tipos <em>"escolha única"</em> e{" "}
														<em>"múltipla escolha"</em> são suportadas
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														Deve haver pelo menos um{" "}
														<span style={{ fontWeight: 600 }}>
															princípio ético
														</span>{" "}
														associado à questão{" "}
														<span style={{ fontSize: "0.75rem" }}>
															(ver mais detalhes abaixo)
														</span>
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														Não podem haver questões repetidas
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														Toda questão tem um{" "}
														<span style={{ fontWeight: 600 }}>peso</span>{" "}
														atribuído que pode ser alterado o valor, sendo o
														padrão 1
													</Typography>
													<ul
														style={{
															margin: 0,
															paddingLeft: "1.5rem",
															paddingTop: "0.2rem",
															display: "flex",
															flexDirection: "column",
															gap: 3,
														}}
													>
														<li>
															<Typography
																variant='body2'
																sx={{ fontSize: "0.8rem" }}
															>
																Pesos são utilizados para contabilizar
																posteriormente os impactos éticos associados a
																cada princípio ético
															</Typography>
														</li>
													</ul>
												</li>
												<li>
													<Typography variant='body2'>
														A remoção de uma seção remove todas as questões
														vinculadas a ela{" "}
														<CustomTooltip
															placement='bottom-end'
															offsetX={0}
															offsetY={-14}
															title={
																<Box
																	sx={{
																		display: "flex",
																		flexDirection: "column",
																		gap: 1,
																		maxWidth: 240,
																	}}
																>
																	<Typography
																		variant='body2'
																		fontSize={"0.8rem"}
																	>
																		Questões só estão vinculadas às seções no{" "}
																		<span style={{ fontWeight: 600 }}>
																			contexto
																		</span>{" "}
																		do questionário.
																	</Typography>
																	<Typography
																		variant='body2'
																		fontSize={"0.8rem"}
																	>
																		Portanto, as seções, em si, não possuem
																		questões vinculadas. Elas são agrupamentos
																		de questões utilizadas somente dentro do
																		questionário.
																	</Typography>
																</Box>
															}
														>
															<Box
																sx={{
																	display: "inline-flex",
																	verticalAlign: "middle",
																	gap: 0.5,
																}}
															>
																<span style={{ fontWeight: 600 }}>
																	no questionário{" "}
																</span>
																<InfoOutlined
																	sx={{
																		width: 18,
																		height: 18,
																		color: blueGrey[500],
																	}}
																/>
															</Box>
														</CustomTooltip>
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														É possível pesquisar e cadastrar novas questões
													</Typography>
												</li>
											</ul>
										</li>
									</ul>
									<ul
										style={{
											margin: 0,
											paddingLeft: "1.5rem",
											paddingTop: "0.2rem",
											display: "flex",
											flexDirection: "column",
											gap: 3,
										}}
									>
										<li>
											<Typography variant='body2'>
												<span style={{ fontWeight: 600 }}>
													Princípios Éticos
												</span>
											</Typography>
											<ul
												style={{
													margin: 0,
													paddingLeft: "1.5rem",
													paddingTop: "0.2rem",
													display: "flex",
													flexDirection: "column",
													gap: 3,
												}}
											>
												<li>
													<Typography variant='body2'>
														Questões poderão ser associadas somente aos
														princípios definidos na etapa de{" "}
														<em>Configurações</em>
													</Typography>
												</li>
												<li>
													<Typography variant='body2'>
														Através da associação à cada questão, peso da
														questão e alternativa marcada pelo respondente, será
														possível calcular o impacto ético associado a cada
														princípio ético{" "}
														<span style={{ fontSize: "0.7rem" }}>
															(Veja o guia de cálculo de impacto ético para
															maiores detalhes)
														</span>
													</Typography>
												</li>
											</ul>
										</li>
									</ul>
								</Box>
							</Box>
						</Box>
					</Collapse>
				</Box>

				<Box>
					<Box
						sx={{
							display: "flex",
							width: "100%",
							alignItems: "center",
							maxWidth: "100%",
							overflow: "hidden",
							gap: 1,
							cursor: "pointer",
						}}
						onClick={() =>
							setExpanded({
								...expanded,
								review: !expanded.review,
							})
						}
					>
						<Typography variant='body2' fontWeight={600}>
							3. Revisão
						</Typography>

						<Divider sx={{ flex: 1, borderColor: blueGrey[100] }} />

						<KeyboardArrowDownSharp
							fontSize='small'
							sx={{
								color: blueGrey[500],
								width: "1.25rem",
								height: "1.25rem",
								transition: "transform 0.3s",
								transform: expanded.review ? "rotate(180deg)" : "rotate(0deg)",
							}}
						/>
					</Box>

					<Collapse in={expanded.review} timeout={"auto"} unmountOnExit>
						<Box
							sx={{
								paddingTop: 0.5,
								paddingInline: 2,
								paddingBottom: 1,
								display: "flex",
								flexDirection: "column",
								gap: 1,
							}}
						>
							<Typography variant='body2'>
								Etapa final de revisão dos dados inseridos no questionário para
								conferência antes da submissão para o Comitê de Avaliação.
							</Typography>
							<Typography variant='body2'>
								Após a submissão ao Comitê, o questionário não poderá ser
								alterado, pois entra em fase de avaliação para{" "}
								<span style={{ fontWeight: 600 }}>homologação</span> ou{" "}
								<span style={{ fontWeight: 600 }}>revisão</span>.
							</Typography>
						</Box>
					</Collapse>
				</Box>
			</Box>

			<Typography variant='body2' sx={{ marginTop: 0.5 }}>
				Uma vez executado o fluxo de cadastro do questionário e tendo submetido
				ao Comitê de Avaliação, ele ficará em fase de análise. Não sendo
				possível a sua alteração até a decisão do Comitê aprovar ou retornar
				para revisão.
			</Typography>

			<Typography variant='body2'>
				Caso o questionário retorne para fins de{" "}
				<span style={{ fontWeight: 600 }}>revisão</span> então ele poderá ser
				editado e submetido novamente para avaliação. Nesse cenário, estará
				disponível o <span style={{ fontWeight: 600 }}>parecer</span> do Comitê
				de Avaliação com as justificativas da revisão.
			</Typography>
		</>
	);
}
